/**
 * /admin/chatbot-quality — Custom Charts + Pre-Auction chatbot quality digest.
 *
 * Data: GET /api/admin/v2/chatbot-quality/?days=&bot=&limit=&only_low_quality=
 * Backend compute in analyzer.services.chatbot_quality (shared with the
 * `manage.py chatbot_digest` CLI, so numbers here match the terminal).
 *
 * Layout — three sections:
 *   Top     : window controls (days · bot filter · low-quality toggle) + refresh
 *   Middle  : summary cards + tool + chart-type distribution (per bot)
 *   Bottom  : worst-N turns (Custom) + flagged assistant replies (Auction)
 *             with expandable prompt / reply preview.
 */
import { useEffect, useMemo, useState } from "react";
import {
  RefreshCw, AlertTriangle, Loader2, MessageSquareWarning,
  ChevronDown, ChevronRight,
} from "lucide-react";
import { getChatbotQuality } from "../../api/owner.api";

const WINDOW_CHOICES = [
  { value: 1,   label: "24h" },
  { value: 7,   label: "7d"  },
  { value: 14,  label: "14d" },
  { value: 30,  label: "30d" },
];
const BOT_CHOICES = [
  { value: "all",     label: "Both bots" },
  { value: "custom",  label: "Custom Charts" },
  { value: "auction", label: "Pre-Auction" },
];

function pct(n) { return n == null ? "—" : `${n}%`; }
function usd(cents) { return `$${((cents || 0) / 100).toFixed(2)}`; }

export default function AdminChatbotQuality() {
  const [days, setDays] = useState(7);
  const [bot, setBot] = useState("all");
  const [onlyLQ, setOnlyLQ] = useState(false);
  const [limit] = useState(25);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, bot, onlyLQ]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await getChatbotQuality({
        days, bot, limit, only_low_quality: onlyLQ ? 1 : 0,
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <MessageSquareWarning className="w-5 h-5" /> Chatbot Quality
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Custom Charts + Pre-Auction chat health over the selected window.
            Powered by <code className="text-xs">chatbot_digest</code> compute.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SegmentedPicker value={days} onChange={setDays} options={WINDOW_CHOICES} />
          <SegmentedPicker value={bot}  onChange={setBot}  options={BOT_CHOICES} />
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border-default text-xs cursor-pointer hover:bg-bg-secondary">
            <input
              type="checkbox"
              className="w-3 h-3"
              checked={onlyLQ}
              onChange={(e) => setOnlyLQ(e.target.checked)}
            />
            Only low-quality
          </label>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-border-default hover:bg-bg-secondary disabled:opacity-50"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-red-500/40 bg-red-500/10 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
          <div>{error}</div>
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 text-text-secondary text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {data?.custom && (
        <CustomSection block={data.custom} />
      )}
      {data?.auction && (
        <AuctionSection block={data.auction} />
      )}

      {data && !data.custom && !data.auction && (
        <div className="text-sm text-text-secondary">No data in the selected window.</div>
      )}
    </div>
  );
}

function SegmentedPicker({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-md border border-border-default overflow-hidden text-xs">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={
            "px-3 py-1.5 " +
            (value === o.value
              ? "bg-indigo-500/25 text-indigo-100 border-r border-border-default last:border-r-0"
              : "hover:bg-bg-secondary border-r border-border-default last:border-r-0")
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function CustomSection({ block }) {
  const s = block.summary || {};
  return (
    <section className="rounded-lg border border-border-default bg-bg-secondary/40">
      <div className="px-4 py-2.5 border-b border-border-default flex items-center justify-between">
        <div className="font-semibold text-sm">Custom Charts</div>
        <div className="text-xs text-text-secondary">
          {s.turns || 0} turn{s.turns === 1 ? "" : "s"} · {usd(s.cost_usd_cents)} · avg {s.avg_latency_ms || 0}ms
        </div>
      </div>
      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <StatCard label="Low quality"  value={pct(s.low_quality_pct)} n={s.low_quality}  tone="rose" />
        <StatCard label="No chart"     value={pct(s.no_chart_pct)}    n={s.no_chart}     tone="amber" />
        <StatCard label="Refused"      value={pct(s.refused_pct)}     n={s.refused}      tone="amber" />
        <StatCard label="Tool errors"  value={pct(s.tool_errors_pct)} n={s.tool_errors}  tone="rose" />
      </div>
      <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <DistributionCard title="Tools called"    rows={block.tool_distribution || []} />
        <DistributionCard title="Chart types emitted" rows={block.chart_type_distribution || []} />
      </div>
      <div className="border-t border-border-default">
        <div className="px-4 py-2 text-xs uppercase tracking-wide text-text-secondary">
          Worst {block.worst?.length || 0} turns
        </div>
        <div className="divide-y divide-border-default">
          {(block.worst || []).map((t) => (
            <CustomTurnRow key={t.id} t={t} />
          ))}
          {(!block.worst || block.worst.length === 0) && (
            <div className="px-4 py-3 text-xs text-text-secondary">Nothing to flag.</div>
          )}
        </div>
      </div>
    </section>
  );
}

function AuctionSection({ block }) {
  const s = block.summary || {};
  return (
    <section className="rounded-lg border border-border-default bg-bg-secondary/40">
      <div className="px-4 py-2.5 border-b border-border-default flex items-center justify-between">
        <div className="font-semibold text-sm">Pre-Auction Chat</div>
        <div className="text-xs text-text-secondary">
          {s.assistant_turns || 0} turn{s.assistant_turns === 1 ? "" : "s"} · {usd(s.cost_usd_cents)}
        </div>
      </div>
      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <StatCard label="Tool turns"   value={s.tool_turns || 0} />
        <StatCard label="Tool errors"  value={pct(s.tool_errors_pct)} n={s.tool_errors} tone="rose" />
        <StatCard label="Scanned"      value={s.scanned || 0} />
        <StatCard label="Flagged"      value={pct(s.flagged_pct)}     n={s.flagged}     tone="amber" />
      </div>
      <div className="px-4 pb-4">
        <DistributionCard title="Tools called" rows={block.tool_distribution || []} />
      </div>
      <div className="border-t border-border-default">
        <div className="px-4 py-2 text-xs uppercase tracking-wide text-text-secondary">
          Flagged assistant replies ({block.flagged?.length || 0})
        </div>
        <div className="divide-y divide-border-default">
          {(block.flagged || []).map((r) => (
            <AuctionFlaggedRow key={r.id} r={r} />
          ))}
          {(!block.flagged || block.flagged.length === 0) && (
            <div className="px-4 py-3 text-xs text-text-secondary">Nothing to flag.</div>
          )}
        </div>
      </div>
    </section>
  );
}

function StatCard({ label, value, n, tone }) {
  const toneClasses = {
    rose:  "border-rose-500/30 bg-rose-500/[0.05]",
    amber: "border-amber-500/30 bg-amber-500/[0.05]",
  };
  return (
    <div className={"rounded-md border border-border-default bg-bg-primary/40 p-3 " + (toneClasses[tone] || "")}>
      <div className="text-[10px] uppercase tracking-wide text-text-secondary">{label}</div>
      <div className="text-lg font-semibold mt-0.5">{value}</div>
      {n != null && <div className="text-[11px] text-text-secondary">({n} in window)</div>}
    </div>
  );
}

function DistributionCard({ title, rows }) {
  if (!rows.length) {
    return (
      <div className="rounded-md border border-border-default p-3 text-xs text-text-secondary">
        <div className="font-medium text-text-primary mb-1">{title}</div>
        <div>—</div>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.count));
  return (
    <div className="rounded-md border border-border-default p-3">
      <div className="font-medium text-sm mb-2">{title}</div>
      <ul className="space-y-1.5 text-xs">
        {rows.map((r) => (
          <li key={r.name} className="flex items-center gap-2">
            <span className="w-40 truncate">{r.name}</span>
            <div className="relative flex-1 h-1.5 rounded bg-bg-primary/60 overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-indigo-500/60"
                style={{ width: `${(r.count / max) * 100}%` }}
              />
            </div>
            <span className="w-8 text-right tabular-nums">{r.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CustomTurnRow({ t }) {
  const [open, setOpen] = useState(false);
  const flags = useMemo(() => {
    const arr = [];
    if (t.likely_refusal)          arr.push("REFUSED");
    if (t.had_tool_error)          arr.push("TOOL_ERR");
    if (!t.chart_spec_present)     arr.push("NO_CHART");
    if (t.zero_row_tool_calls)     arr.push(`${t.zero_row_tool_calls} zero`);
    if (t.stop_reason === "max_tokens") arr.push("TRUNCATED");
    return arr;
  }, [t]);
  return (
    <div className="px-4 py-2.5 text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left flex items-start gap-2"
      >
        {open
          ? <ChevronDown className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          : <ChevronRight className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-text-secondary">
              {new Date(t.created_at).toLocaleString()}
            </span>
            <span className="text-text-secondary">·</span>
            <span>{t.user || "?"}</span>
            <span className="text-text-secondary">· hops {t.hops} · {t.elapsed_ms}ms · {usd(t.cost_usd_cents)}</span>
            {flags.map((f) => (
              <span key={f} className="px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 text-[10px]">
                {f}
              </span>
            ))}
          </div>
          <div className="truncate text-text-primary mt-1">
            Q: {t.prompt}
          </div>
        </div>
      </button>
      {open && (
        <div className="mt-2 ml-6 space-y-1.5">
          {t.tool_names?.length > 0 && (
            <div className="text-text-secondary">Tools: {t.tool_names.join(", ")}</div>
          )}
          {t.competitions_used?.length > 0 && (
            <div className="text-text-secondary">
              Competitions: {t.competitions_used.map((c) => c.name || c.id).join(", ")}
            </div>
          )}
          <div className="whitespace-pre-wrap text-text-primary">A: {t.reply}</div>
          <div className="text-[10px] text-text-secondary select-all">turn_id={t.id}</div>
        </div>
      )}
    </div>
  );
}

function AuctionFlaggedRow({ r }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="px-4 py-2.5 text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left flex items-start gap-2"
      >
        {open
          ? <ChevronDown className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          : <ChevronRight className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-text-secondary">{new Date(r.created_at).toLocaleString()}</span>
            <span className="text-text-secondary">· thread {r.thread_id.slice(0, 8)}</span>
            {r.reasons?.map((f) => (
              <span key={f} className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[10px]">
                {f}
              </span>
            ))}
          </div>
          <div className="truncate mt-1">A: {r.text}</div>
        </div>
      </button>
      {open && (
        <div className="mt-2 ml-6 whitespace-pre-wrap text-text-primary">
          {r.text}
        </div>
      )}
    </div>
  );
}
